import type { TeamBillingResponse } from '../api';
import { errorMessage } from '../api/problems';
import { useSession } from '../auth/session';
import { Banner, Button, Spinner } from '../components/ui';
import {
  formatPrice,
  useCancelPlan,
  useCheckout,
  usePlan,
  usePortal,
  useTeamBilling,
} from '../library/billingQueries';
import { isCheckoutConfigured } from './paddle';

/**
 * A team's plan, seats and state.
 *
 * Every member sees it, not just owners. A library that has quietly gone read-only needs to say so
 * to the people it stopped working for, or they file a bug against the editor; only the owner gets
 * the buttons that spend money.
 */
export function TeamBillingPanel({ teamId }: { teamId: string }) {
  const plan = usePlan();
  const state = useTeamBilling(teamId);

  if (plan.data && !plan.data.enabled) {
    // This deployment charges for nothing. Showing an inert plan panel would only raise questions.
    return null;
  }

  if (state.isPending || plan.isPending) {
    return <Spinner label="Loading billing" />;
  }

  if (state.isError || !state.data) {
    return <Banner tone="error">{errorMessage(state.error, 'Billing could not be loaded.')}</Banner>;
  }

  // Keyed by team: the mutation hooks below hold their own error state, and without a remount a
  // failure on one team stays on screen while you look at another one's plan.
  return <Panel key={teamId} teamId={teamId} billing={state.data} />;
}

function Panel({ teamId, billing }: { teamId: string; billing: TeamBillingResponse }) {
  const plan = usePlan();
  const { data: me } = useSession();

  const checkout = useCheckout(teamId);
  const portal = usePortal(teamId);
  const cancel = useCancelPlan(teamId);

  // Zero means the provider could not be reached for a price. Showing "£0.00 per person" would be
  // worse than showing nothing.
  const price =
    plan.data && plan.data.amountMinorUnits > 0
      ? `${formatPrice(plan.data.amountMinorUnits, plan.data.currencyCode)} per person per ${plan.data.interval}`
      : '';

  const payable = billing.seatsUsed > billing.freeSeats;
  const busy = checkout.isPending || portal.isPending || cancel.isPending;

  // Cancelling schedules a change rather than ending anything today, and the provider then locks
  // the subscription against further changes. Offering Cancel again would earn a refusal.
  const pendingCancellation = billing.scheduledChange === 'Cancel';

  return (
    <section className="flex flex-col gap-3 rounded-md border border-border-subtle p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-medium text-text">Plan</h3>
        <span className="text-xs text-text-faint">{price}</span>
      </div>

      <StateBanner billing={billing} />

      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
        <dt className="text-text-muted">People</dt>
        <dd className="text-text">
          {billing.seatsUsed}
          {!payable && ` of ${billing.freeSeats} free`}
        </dd>

        {/* Only while money is actually moving. A cancelled subscription keeps its last seat
            count, and showing it reads as an ongoing bill for a plan that ended. */}
        {billing.isBilling && (
          <>
            <dt className="text-text-muted">Seats billed</dt>
            <dd className="text-text">{billing.seatsBilled}</dd>
          </>
        )}

        {pendingCancellation ? (
          <>
            <dt className="text-text-muted">Cancels</dt>
            <dd className="text-text">
              {billing.scheduledChangeAt
                ? new Date(billing.scheduledChangeAt).toLocaleDateString()
                : 'at the end of this period'}
            </dd>
          </>
        ) : (
          billing.currentPeriodEndsAt && (
            <>
              <dt className="text-text-muted">
                {billing.status === 'Canceled' ? 'Access until' : 'Renews'}
              </dt>
              <dd className="text-text">
                {new Date(billing.currentPeriodEndsAt).toLocaleDateString()}
              </dd>
            </>
          )
        )}
      </dl>

      {(checkout.isError || portal.isError || cancel.isError) && (
        <Banner tone="error">
          {errorMessage(checkout.error ?? portal.error ?? cancel.error)}
        </Banner>
      )}

      {/* Cancelling schedules something for weeks away, so without this the click has no visible
          effect at all — the plan is still active, still says Renews, and the only way to find out
          it worked was to click again and be refused. */}
      {cancel.isSuccess && !pendingCancellation && (
        <Banner tone="success">
          Cancellation scheduled. The plan keeps working until the end of the period you have paid
          for.
        </Banner>
      )}

      {/* Paid, but the provider has not told our server yet. Silently returning to a Subscribe
          button here reads as the payment having failed, and invites paying twice. */}
      {checkout.data?.outcome === 'completed' && !checkout.data.confirmed && (
        <Banner tone="error">
          Your payment went through, but confirmation from the payment provider has not arrived
          yet. This usually settles within a minute — reload the page then. Do not pay again.
        </Banner>
      )}

      {billing.canManageBilling ? (
        <div className="flex flex-wrap gap-2">
          {!billing.hasSubscription || billing.status === 'Canceled' ? (
            <Button
              variant="primary"
              disabled={busy || !isCheckoutConfigured()}
              onClick={() => checkout.mutate(me?.email)}
            >
              {/* Stays pending through the wait for the provider to confirm, which is the only
                  part of this the customer actually watches — the overlay covers the rest. */}
              {checkout.isPending ? 'Confirming…' : 'Subscribe'}
            </Button>
          ) : (
            <>
              {/* Deep-links to this team's subscription, so the payer never has to pick it out of
                  a list of identically-named ones. */}
              <Button disabled={busy} onClick={() => portal.mutate('paymentMethod')}>
                Update card
              </Button>

              <Button variant="ghost" disabled={busy} onClick={() => portal.mutate('overview')}>
                Invoices &amp; account
              </Button>

              {!pendingCancellation && (
                <Button
                  variant="ghost"
                  disabled={busy}
                  onClick={() => {
                    if (window.confirm('Cancel at the end of the paid period?')) {
                      cancel.mutate();
                    }
                  }}
                >
                  Cancel plan
                </Button>
              )}
            </>
          )}
        </div>
      ) : (
        <p className="text-xs text-text-faint">Only an owner of this team can change the plan.</p>
      )}
    </section>
  );
}

function StateBanner({ billing }: { billing: TeamBillingResponse }) {
  if (billing.scheduledChange === 'Cancel') {
    const when = billing.scheduledChangeAt
      ? ` on ${new Date(billing.scheduledChangeAt).toLocaleDateString()}`
      : ' at the end of the current period';

    return <Banner>This plan is set to cancel{when}. Everything works normally until then.</Banner>;
  }

  if (billing.state === 'Locked') {
    return (
      <Banner tone="error">
        This library is read-only. Everything can still be read and copied — changes resume once
        the plan is sorted out, or once the team is back to {billing.freeSeats} people or fewer.
      </Banner>
    );
  }

  if (billing.state === 'InGrace') {
    const until = billing.graceEndsAt
      ? ` until ${new Date(billing.graceEndsAt).toLocaleDateString()}`
      : '';

    return (
      <Banner tone="error">
        A payment did not go through. The library keeps working{until}.
      </Banner>
    );
  }

  if (billing.seatsUsed >= billing.freeSeats && !billing.hasSubscription) {
    return (
      <Banner>
        This team is using its last free seat. Adding anybody else needs a subscription.
      </Banner>
    );
  }

  return null;
}
